import React from "react";
import { Compass, Sun, PackageCheck, Globe2 } from "lucide-react";

const WHY_IMG =
  "https://images.unsplash.com/photo-1771617724150-d77e8e426190?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxhdGhsZXRlJTIwcnVubmluZyUyMHNhbmR8ZW58MHx8fHwxNzgyMTg4NTI2fDA&ixlib=rb-4.1.0&q=85";

const PILLARS = [
  {
    icon: Compass,
    title: "Curated, not catalogued",
    body: "We stock a small list of products we’d serve at our own table. No SKU bloat, no filler items.",
  },
  {
    icon: Sun,
    title: "Built for UAE conditions",
    body: "Heat, humidity, sweat rate, dust. Every product is tested against the climate it’s sold in.",
  },
  {
    icon: PackageCheck,
    title: "Fast UAE delivery",
    body: "Stock held locally in Dubai. Order today, train with it tomorrow.",
  },
  {
    icon: Globe2,
    title: "Sourced globally",
    body: "From Japanese konjac fields to European salt mines — we go where the product is best.",
  },
];

export const WhyUs = () => {
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="relative bg-forest text-bone py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Heading column */}
          <div className="lg:col-span-5">
            <div className="overline text-terracotta mb-5" data-testid="why-us-overline">
              Our Standard
            </div>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              Curated for the
              <br />
              <span className="font-wordmark italic font-semibold text-bone/95">
                UAE’s active elite.
              </span>
            </h2>
            <p className="mt-7 text-bone/75 text-base md:text-lg leading-relaxed max-w-md">
              Thrive by Madsen is a quiet rebellion against generic wellness
              shelves. We bring in what athletes, runners and considered eaters
              ask for — and can’t get here easily.
            </p>
          </div>

          {/* Bento right */}
          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4 auto-rows-[minmax(140px,auto)]">
            {/* Image cell - spans 2x2 */}
            <div className="col-span-2 md:col-span-3 row-span-2 relative overflow-hidden rounded-sm bg-black/40">
              <img
                src={WHY_IMG}
                alt="Athlete running on UAE sand at sunset"
                className="w-full h-full object-cover opacity-90"
                loading="lazy"
                data-testid="why-us-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/40 to-transparent"></div>
              <div className="absolute bottom-5 left-5 right-5">
                <p className="font-wordmark italic text-bone/95 text-xl md:text-2xl leading-snug">
                  “The desert doesn’t forgive shortcuts.”
                </p>
              </div>
            </div>

            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={i}
                  data-testid={`why-us-pillar-${i}`}
                  className="col-span-2 md:col-span-3 border border-bone/15 bg-bone/[0.03] hover:bg-bone/[0.06] transition-colors duration-300 p-5 rounded-sm"
                >
                  <Icon className="w-5 h-5 text-terracotta mb-3" strokeWidth={1.75} />
                  <h3 className="font-display font-bold text-bone text-base md:text-lg leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-bone/65 text-sm leading-relaxed">
                    {p.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
